import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock, Download, MoreVertical, Search, TrendingUp, Users, Wine, AlertTriangle, CheckCircle, XCircle, Copy, Edit, Trash2, X } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { NavBar } from '../components/NavBar';
import { Button } from '../components/SubscriptionAppButton';
import { Badge } from '../components/SubscriptionAppBadge';
import { Card } from '../components/SubscriptionAppCard';
import { StatCard } from '../components/StatCard';
import { Input } from '../components/SubscriptionAppInput';
import { Checkbox } from '../components/Checkbox';
import { Divider } from '../components/SubscriptionAppDivider';
import { Pagination } from '../components/SubscriptionAppPagination';
import { TableFilter, Filter } from '../components/TableFilter';
import { ExportButton } from '../components/ExportButton';
// Mock Data
const allocationData = {
  id: '1',
  name: '2023 Reserve Shiraz Release',
  status: 'Active',
  startDate: '2026-03-01',
  endDate: '2026-03-15',
  earlyAccessDate: '2026-02-27',
  earlyAccessTier: 'Vintner',
  totalAvailable: 200,
  totalClaimed: 142,
  revenue: 28400,
  eligibleCount: 485,
  purchasedCount: 142,
  avgOrderValue: 200
};
const productsData = [{
  id: 'p1',
  name: '2023 Reserve Shiraz',
  variant: '750ml',
  sku: 'SHZ-23-RES',
  allocated: 200,
  claimed: 128,
  price: 120
}, {
  id: 'p2',
  name: '2023 Reserve Shiraz',
  variant: 'Magnum',
  sku: 'SHZ-23-RES-MAG',
  allocated: 50,
  claimed: 14,
  price: 300
}];
const claimVelocityData = [{
  date: '1 Mar',
  claims: 12
}, {
  date: '2 Mar',
  claims: 45
}, {
  date: '3 Mar',
  claims: 89
}, {
  date: '4 Mar',
  claims: 112
}, {
  date: '5 Mar',
  claims: 128
}, {
  date: '6 Mar',
  claims: 135
}, {
  date: '7 Mar',
  claims: 142
} // Today
];
const membersData = [{
  id: 'm1',
  name: 'Sarah Johnson',
  email: 'sarah.j@example.com',
  tier: 'Sommelier',
  entitlement: 'Max 6 bottles',
  purchased: 6,
  remaining: 0,
  status: 'Exhausted',
  lastActivity: '2 hours ago'
}, {
  id: 'm2',
  name: 'Michael Chen',
  email: 'michael.c@example.com',
  tier: 'Connoisseur',
  entitlement: 'Max 6 bottles',
  purchased: 0,
  remaining: 6,
  status: 'Invited',
  lastActivity: '-'
}, {
  id: 'm3',
  name: 'Emma Williams',
  email: 'emma.w@example.com',
  tier: 'Vintner',
  entitlement: 'Max 12 bottles',
  purchased: 6,
  remaining: 6,
  status: 'Purchased',
  lastActivity: '1 day ago'
}, {
  id: 'm4',
  name: 'James Anderson',
  email: 'james.a@example.com',
  tier: 'Enthusiast',
  entitlement: 'Max 3 bottles',
  purchased: 3,
  remaining: 0,
  status: 'Exhausted',
  lastActivity: '3 days ago'
}, {
  id: 'm5',
  name: 'Olivia Martinez',
  email: 'olivia.m@example.com',
  tier: 'Sommelier',
  entitlement: 'Max 6 bottles',
  purchased: 2,
  remaining: 4,
  status: 'Purchased',
  lastActivity: '5 hours ago'
}, {
  id: 'm6',
  name: 'William Taylor',
  email: 'w.taylor@example.com',
  tier: 'Connoisseur',
  entitlement: 'Max 6 bottles',
  purchased: 0,
  remaining: 6,
  status: 'Expired',
  lastActivity: '1 week ago'
}, {
  id: 'm7',
  name: 'Sophia Brown',
  email: 'sophia.b@example.com',
  tier: 'Enthusiast',
  entitlement: 'Max 3 bottles',
  purchased: 1,
  remaining: 2,
  status: 'Purchased',
  lastActivity: 'Yesterday'
}, {
  id: 'm8',
  name: 'Liam Wilson',
  email: 'liam.w@example.com',
  tier: 'Vintner',
  entitlement: 'Max 12 bottles',
  purchased: 12,
  remaining: 0,
  status: 'Exhausted',
  lastActivity: '4 days ago'
}];
const ordersData = [{
  id: '#4521',
  member: 'Sarah Johnson',
  products: '6x Reserve Shiraz',
  qty: 6,
  total: 720,
  date: 'Mar 7, 2026'
}, {
  id: '#4520',
  member: 'Emma Williams',
  products: '6x Reserve Shiraz',
  qty: 6,
  total: 720,
  date: 'Mar 6, 2026'
}, {
  id: '#4519',
  member: 'James Anderson',
  products: '3x Reserve Shiraz',
  qty: 3,
  total: 360,
  date: 'Mar 4, 2026'
}, {
  id: '#4518',
  member: 'Olivia Martinez',
  products: '2x Reserve Shiraz',
  qty: 2,
  total: 240,
  date: 'Mar 7, 2026'
}, {
  id: '#4517',
  member: 'Sophia Brown',
  products: '1x Reserve Shiraz',
  qty: 1,
  total: 120,
  date: 'Mar 6, 2026'
}];
export function AllocationDetailPage() {
  const navigate = useNavigate();
  const {
    id
  } = useParams();
  // State
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [duplicateName, setDuplicateName] = useState(`${allocationData.name} (Copy)`);
  const [notifyOnClose, setNotifyOnClose] = useState(true);
  const [filterValues, setFilterValues] = useState<Record<string, string[]>>({
    status: [],
    tier: []
  });
  const [openFilterDropdowns, setOpenFilterDropdowns] = useState<Set<string>>(new Set());
  const [addedFilters, setAddedFilters] = useState<Set<string>>(new Set());
  // Filters Configuration
  const availableFilters: Filter[] = [{
    id: 'status',
    label: 'Status',
    options: [{
      id: 'invited',
      label: 'Invited',
      value: 'Invited'
    }, {
      id: 'purchased',
      label: 'Purchased',
      value: 'Purchased'
    }, {
      id: 'exhausted',
      label: 'Exhausted',
      value: 'Exhausted'
    }, {
      id: 'expired',
      label: 'Expired',
      value: 'Expired'
    }],
    selectedValues: filterValues.status || [],
    multiSelect: true,
    isOpen: openFilterDropdowns.has('status')
  }, {
    id: 'tier',
    label: 'Tier',
    options: [{
      id: 'vintner',
      label: 'Vintner',
      value: 'Vintner'
    }, {
      id: 'sommelier',
      label: 'Sommelier',
      value: 'Sommelier'
    }, {
      id: 'connoisseur',
      label: 'Connoisseur',
      value: 'Connoisseur'
    }, {
      id: 'enthusiast',
      label: 'Enthusiast',
      value: 'Enthusiast'
    }],
    selectedValues: filterValues.tier || [],
    multiSelect: true,
    isOpen: openFilterDropdowns.has('tier')
  }];
  // Handlers
  const handleFilterChange = (filterId: string, value: string | string[]) => {
    setFilterValues(prev => ({
      ...prev,
      [filterId]: Array.isArray(value) ? value : [value]
    }));
  };
  const handleFilterRemove = (filterId: string) => {
    const newAdded = new Set(addedFilters);
    newAdded.delete(filterId);
    setAddedFilters(newAdded);
    setFilterValues(prev => ({
      ...prev,
      [filterId]: []
    }));
  };
  const handleToggleFilterDropdown = (filterId: string) => {
    const newOpen = new Set(openFilterDropdowns);
    if (newOpen.has(filterId)) {
      newOpen.delete(filterId);
    } else {
      newOpen.add(filterId);
    }
    setOpenFilterDropdowns(newOpen);
  };
  const handleAddFilter = (filterId: string) => {
    const newAdded = new Set(addedFilters);
    newAdded.add(filterId);
    setAddedFilters(newAdded);
  };
  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'Purchased':
        return 'success';
      case 'Exhausted':
        return 'info';
      case 'Expired':
        return 'error';
      default:
        return 'default';
    }
  };
  const getTierColor = (tier: string) => {
    switch (tier) {
      case 'Vintner':
        return 'bg-[#FEF3C7] text-[#D97706] border-[#FDE68A]';
      case 'Sommelier':
        return 'bg-[#EDE9FE] text-[#7C3AED] border-[#DDD6FE]';
      case 'Connoisseur':
        return 'bg-[#DBEAFE] text-[#2563EB] border-[#BFDBFE]';
      default:
        return 'bg-[#D1FAE5] text-[#059669] border-[#A7F3D0]';
    }
  };
  const getProgressColor = (claimed: number, total: number) => {
    const percentage = claimed / total * 100;
    if (percentage >= 90) return 'bg-[#EF4444]';
    if (percentage >= 75) return 'bg-[#F59E0B]';
    return 'bg-[#10B981]';
  };
  return <div className="bg-[#F1F5F9] min-h-full p-6" data-id="element-6454" data-mp="pv29p7yg8bbo">
      {/* NavBar */}
      <div className="mb-6" data-id="element-6455" data-mp="puabrqhdq1i8">
        <NavBar tabs={[]} title="Allocation Details" data-id="element-6456" data-mp="kvz2bguzxt0j" />
      </div>

      {/* Header */}
      <div className="flex flex-col gap-6 mb-8" data-id="element-6457" data-mp="kybltygekbfp">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4" data-id="element-6458" data-mp="g47dbarhnvci">
          <div className="flex items-center gap-4" data-id="element-6459" data-mp="c2vubo34e8da">
            <button onClick={() => navigate('/allocations')} className="p-2 hover:bg-white rounded-full transition-colors text-[#64748B] hover:text-[#0F172A]" data-id="element-6460" data-mp="74km1hsh45x6">
              <ArrowLeft className="w-5 h-5" data-id="element-6461" data-mp="23wm26m9oeqt" />
            </button>
            <div data-id="element-6462" data-mp="3tb0qctr11mvj">
              <div className="flex items-center gap-3" data-id="element-6463" data-mp="3tck7emkic1yd">
                <h1 className="text-2xl font-bold text-[#0F172A]" data-id="element-6464" data-mp="3pagqvjize768">
                  {allocationData.name}
                </h1>
                <Badge variant="success" data-id="element-6465" data-mp="1rje578ttgm6z">Active</Badge>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3" data-id="element-6466" data-mp="1nk5d08efcwdj">
            <Button variant="primary" iconLeft={<Edit className="w-[18px] h-[18px]" data-id="element-6468" data-mp="35fe3zkbup7vc" />} onClick={() => navigate(`/allocations/${id}/edit`)} data-id="element-6467" data-mp="35ggs5khsgw7k">
              Edit
            </Button>
            <Button variant="primary" iconLeft={<Copy className="w-[18px] h-[18px]" data-id="element-6470" data-mp="3f5qml703uqyu" />} onClick={() => {
            setDuplicateName(`${allocationData.name} (Copy)`);
            setShowDuplicateModal(true);
          }} data-id="element-6469" data-mp="3adfli0tmoz4p">
              Duplicate
            </Button>
            <Button variant="danger" iconLeft={<XCircle className="w-[18px] h-[18px]" data-id="element-6472" data-mp="2mjxrzkoyqin5" />} onClick={() => setShowCloseModal(true)} data-id="element-6471" data-mp="2miwe78gkbi1q">
              Close Allocation
            </Button>
          </div>
        </div>

        {/* Info Bar */}
        <div className="bg-white border border-[#E2E8F0] rounded-lg p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 shadow-sm" data-id="element-6473" data-mp="2vkm76iq3ep1m">
          <div className="flex items-center gap-6" data-id="element-6474" data-mp="ar8z77nuwiuq">
            <div className="flex items-center gap-2 text-[#0F172A]" data-id="element-6475" data-mp="fr55jdk10mdg">
              <Calendar className="w-5 h-5 text-[#64748B]" data-id="element-6476" data-mp="ft82yinidhs7" />
              <span className="font-medium" data-id="element-6477" data-mp="2xlmnb1na8rke">1 March 2026 — 15 March 2026</span>
            </div>
            <div className="flex items-center gap-2 text-[#0EA5E9] bg-[#E0F2FE] px-3 py-1 rounded-full text-sm font-medium" data-id="element-6478" data-mp="32kzbpqtsy6p3">
              <Clock className="w-4 h-4" data-id="element-6479" data-mp="37czdzpxchbou" />8 days remaining
            </div>
          </div>

          <div className="flex items-center gap-2 text-sm text-[#64748B]" data-id="element-6480" data-mp="3ca93t6onbpd7">
            <span className="font-medium text-[#0F172A]" data-id="element-6481" data-mp="2eolhxfptgpfx">Early access:</span>
            <span data-id="element-6482" data-mp="2jmmwbncas0l2">27 Feb</span>
            <span className="bg-[#FEF3C7] text-[#D97706] px-2 py-0.5 rounded text-xs font-medium border border-[#FDE68A]" data-id="element-6483" data-mp="2jny0ho9nnacp">
              Vintner tier
            </span>
          </div>
        </div>
      </div>

      {/* ROW 1: KPI Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-8" data-id="element-6484" data-mp="2slgsxdpmoqym">
        <StatCard label="Total Claimed" value={`${allocationData.totalClaimed} / ${allocationData.totalAvailable}`} trend={{
        value: '71%',
        isPositive: true
      }} data-id="element-6485" data-mp="3vh83o14uer2" />
        <StatCard label="Revenue" value={`$${allocationData.revenue.toLocaleString()}`} data-id="element-6486" data-mp="1se3bisb2w33p" />
        <StatCard label="Eligible Members" value={allocationData.eligibleCount} data-id="element-6487" data-mp="1oeuixqrryb9q" />
        <StatCard label="Purchased" value={`${allocationData.purchasedCount} members`} trend={{
        value: '29% conv.',
        isPositive: true
      }} data-id="element-6488" data-mp="1jm9p4p8vby67" />
        <StatCard label="Avg. Order Value" value={`$${allocationData.avgOrderValue}`} data-id="element-6489" data-mp="1em5hzptuofsn" />
      </div>

      {/* ROW 2: Products & Velocity */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 mb-8" data-id="element-6490" data-mp="14pixj25frqr5">
        {/* Products Table */}
        <div className="lg:col-span-3" data-id="element-6491" data-mp="10nfarhdbgzqk">
          <Card padding="none" className="h-full flex flex-col" data-id="element-6492" data-mp="10ogwqgvvf6n2">
            <div className="p-6 border-b border-[#E2E8F0]" data-id="element-6493" data-mp="vq5oubp4z67h">
              <h2 className="text-lg font-semibold text-[#0F172A]" data-id="element-6494" data-mp="2v281p33992c6">Products</h2>
            </div>
            <div className="flex-1 overflow-x-auto" data-id="element-6495" data-mp="2q3594i4gool3">
              <table className="w-full" data-id="element-6496" data-mp="2q4g9s92m1g7z">
                <thead className="bg-[#F8FAFC]" data-id="element-6497" data-mp="397ry6l6lwji9">
                  <tr data-id="element-6498" data-mp="396ynxa8fvbvd">
                    <th className="px-6 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-6499" data-mp="396gusmh2pdro">
                      Product
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-6500" data-mp="308d7plqfw8xr">
                      Allocated
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider w-32" data-id="element-6501" data-mp="3svhi668axfgx">
                      Claimed
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-6502" data-mp="3ixtmne2vg3xp">
                      Remaining
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-6503" data-mp="3nyr05dcxs9sv">
                      Revenue
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E2E8F0]" data-id="element-6504" data-mp="24xymdof4623o">
                  {productsData.map(product => {
                  const percentage = Math.round(product.claimed / product.allocated * 100);
                  return <tr key={product.id} className="hover:bg-[#F8FAFC]" data-id="element-6505" data-mp="2dz6h5wif8kt0">
                        <td className="px-6 py-4" data-id="element-6506" data-mp="2dwag3zba1g0c">
                          <div className="flex items-center gap-3" data-id="element-6507" data-mp="290v7bls4maas">
                            <div className="w-8 h-8 rounded bg-[#F1F5F9] flex items-center justify-center flex-shrink-0" data-id="element-6508" data-mp="1l9heleq2ccpa">
                              <Wine className="w-4 h-4 text-[#94A3B8]" data-id="element-6509" data-mp="1h9fa6xsy156b" />
                            </div>
                            <div data-id="element-6510" data-mp="1v0kggiqtgvpm">
                              <div className="text-sm font-medium text-[#0F172A]" data-id="element-6511" data-mp="1v1vfy6d8bhax">
                                {product.name}
                              </div>
                              <div className="text-xs text-[#64748B]" data-id="element-6512" data-mp="12i6pn33wea01">
                                {product.variant}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-right text-sm text-[#64748B]" data-id="element-6513" data-mp="1g686ttfcrcz0">
                          {product.allocated}
                        </td>
                        <td className="px-6 py-4" data-id="element-6514" data-mp="1l3qjnuwhqcca">
                          <div className="w-full" data-id="element-6515" data-mp="1pvrst7yd3my3">
                            <div className="flex justify-between text-xs mb-1" data-id="element-6516" data-mp="1pxcjgmhk2lqo">
                              <span className="font-medium text-[#0F172A]" data-id="element-6517" data-mp="1twtsfpw6y10l">
                                {product.claimed}
                              </span>
                              <span className="text-[#64748B]" data-id="element-6518" data-mp="23w2kgd7hx5kb">
                                {percentage}%
                              </span>
                            </div>
                            <div className="h-1.5 w-full bg-[#E2E8F0] rounded-full overflow-hidden" data-id="element-6519" data-mp="28sjefo1dme5b">
                              <div className={`h-full rounded-full ${getProgressColor(product.claimed, product.allocated)}`} style={{
                            width: `${percentage}%`
                          }} data-id="element-6520" data-mp="eguzhlv5abkx" />
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-right text-sm font-medium text-[#0F172A]" data-id="element-6521" data-mp="2iykq0acwpk8k">
                          {product.allocated - product.claimed}
                        </td>
                        <td className="px-6 py-4 text-right text-sm font-medium text-[#0F172A]" data-id="element-6522" data-mp="3aq09hvj62quk">
                          ${(product.claimed * product.price).toLocaleString()}
                        </td>
                      </tr>;
                })}
                </tbody>
                <tfoot className="bg-[#F8FAFC] border-t border-[#E2E8F0]" data-id="element-6523" data-mp="31nhbppu8n4f6">
                  <tr data-id="element-6524" data-mp="2wpfpwx5fgr00">
                    <td className="px-6 py-3 text-sm font-bold text-[#0F172A]" data-id="element-6525" data-mp="2wpz72bn97bcz">
                      Totals
                    </td>
                    <td className="px-6 py-3 text-right text-sm font-bold text-[#0F172A]" data-id="element-6526" data-mp="1lbbz1zu4ziz6">
                      {productsData.reduce((sum, p) => sum + p.allocated, 0)}
                    </td>
                    <td className="px-6 py-3" data-id="element-6527" data-mp="1rrc87cxb6qh9"></td>
                    <td className="px-6 py-3 text-right text-sm font-bold text-[#0F172A]" data-id="element-6528" data-mp="1rqslzk0mlfxf">
                      {productsData.reduce((sum, p) => sum + (p.allocated - p.claimed), 0)}
                    </td>
                    <td className="px-6 py-3 text-right text-sm font-bold text-[#0F172A]" data-id="element-6529" data-mp="1ip2y2o5ycz4e">
                      $
                      {productsData.reduce((sum, p) => sum + p.claimed * p.price, 0).toLocaleString()}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </Card>
        </div>

        {/* Claim Velocity Chart */}
        <div className="lg:col-span-2" data-id="element-6530" data-mp="z36iebreebak">
          <Card className="h-full flex flex-col" data-id="element-6531" data-mp="l2vqd5ecqqnt">
            <div className="flex justify-between items-start mb-6" data-id="element-6532" data-mp="l46tr43vxwee">
              <div data-id="element-6533" data-mp="q2i3leu6k6wm">
                <h2 className="text-lg font-semibold text-[#0F172A]" data-id="element-6534" data-mp="q0xcjz72r63i">
                  Claim Velocity
                </h2>
                <p className="text-sm text-[#64748B]" data-id="element-6535" data-mp="6gf6323p3uvs">
                  Cumulative units claimed over time
                </p>
              </div>
              <div className="text-right" data-id="element-6536" data-mp="gew24dhhqzop">
                <div className="text-sm font-medium text-[#64748B]" data-id="element-6537" data-mp="gfxohe3yj8lq">
                  Projected sell-out
                </div>
                <div className="text-sm font-bold text-[#0EA5E9]" data-id="element-6538" data-mp="bhmf14hkr041">12 Mar</div>
              </div>
            </div>

            <div className="flex-1 min-h-[200px]" data-id="element-6539" data-mp="kg7toojxduzj">
              <ResponsiveContainer width="100%" height="100%" data-id="element-6540" data-mp="kfev4av4ebsh">
                <AreaChart data={claimVelocityData} margin={{
                top: 10,
                right: 10,
                left: -20,
                bottom: 0
              }} data-id="element-6541" data-mp="z53zsg2f8xxi">
                  <defs data-id="element-6542" data-mp="185960mvvpqa3">
                    <linearGradient id="colorClaims" x1="0" y1="0" x2="0" y2="1" data-id="element-6543" data-mp="186k9smpbqy17">
                      <stop offset="5%" stopColor="#0EA5E9" stopOpacity={0.1} data-id="element-6544" data-mp="144guto3g5bb6" />
                      <stop offset="95%" stopColor="#0EA5E9" stopOpacity={0} data-id="element-6545" data-mp="2aivduzfflytb" />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" data-id="element-6546" data-mp="2fgwvdfx7lq2o" />
                  <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{
                  fontSize: 12,
                  fill: '#94A3B8'
                }} dy={10} data-id="element-6547" data-mp="2ki47fr6dxbf5" />
                  <YAxis axisLine={false} tickLine={false} tick={{
                  fontSize: 12,
                  fill: '#94A3B8'
                }} data-id="element-6548" data-mp="1rsnemseursie" />
                  <Tooltip contentStyle={{
                  borderRadius: '8px',
                  border: '1px solid #E2E8F0',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                }} data-id="element-6549" data-mp="1wrqapncuuoe8" />
                  <ReferenceLine y={200} stroke="#94A3B8" strokeDasharray="3 3" label={{
                  value: 'Cap (200)',
                  position: 'insideTopRight',
                  fill: '#94A3B8',
                  fontSize: 12
                }} data-id="element-6550" data-mp="25lmwh123xyt4" />
                  <Area type="monotone" dataKey="claims" stroke="#0EA5E9" strokeWidth={2} fillOpacity={1} fill="url(#colorClaims)" data-id="element-6551" data-mp="15ioutz0pmiqj" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </div>
      </div>

      {/* ROW 3: Members Table */}
      <div className="mb-8" data-id="element-6552" data-mp="rj5mp67wf7e8">
        <Card padding="none" data-id="element-6553" data-mp="rjxj2j9bl95k">
          <div className="p-6 border-b border-[#E2E8F0]" data-id="element-6554" data-mp="ml438k6k9628">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-6555" data-mp="mmozqfcfqz2q">
              <h2 className="text-lg font-semibold text-[#0F172A]" data-id="element-6556" data-mp="ht2w1co925j2">Members</h2>

              <div className="flex items-center gap-3 w-full sm:w-auto" data-id="element-6557" data-mp="cw432xgbo4mg">
                <div className="w-full sm:w-64" data-id="element-6558" data-mp="8u925u2azt97">
                  <Input placeholder="Search members..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} icon={<Search className="w-4 h-4" data-id="element-6560" data-mp="3wh0w9frqusc" />} data-id="element-6559" data-mp="8vk4dpg3osy7" />
                </div>
                <TableFilter filters={availableFilters} onFilterChange={handleFilterChange} onFilterRemove={handleFilterRemove} onToggleDropdown={handleToggleFilterDropdown} addedFilterIds={addedFilters} onAddFilter={handleAddFilter} renderOnlyButton={true} data-id="element-6561" data-mp="3v1foyl5j863c" />
              </div>
            </div>

            {addedFilters.size > 0 && <div className="mt-4" data-id="element-6562" data-mp="3jwhluq9ad1kf">
                <TableFilter filters={availableFilters} onFilterChange={handleFilterChange} onFilterRemove={handleFilterRemove} onToggleDropdown={handleToggleFilterDropdown} addedFilterIds={addedFilters} onAddFilter={handleAddFilter} renderOnlyChips={true} data-id="element-6563" data-mp="3evtolmugrohr" />
              </div>}
          </div>

          <div className="overflow-x-auto" data-id="element-6564" data-mp="313drt3s5ompa">
            <table className="w-full" data-id="element-6565" data-mp="314f1b3tix14w">
              <thead className="bg-[#F8FAFC]" data-id="element-6566" data-mp="2w4u1kr6j4or4">
                <tr data-id="element-6567" data-mp="2w655cqzz5wi8">
                  <th className="px-6 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-6568" data-mp="2w2pqnpa7qphc">
                    Member
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-6569" data-mp="2n574uioqw74e">
                    Tier
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-6570" data-mp="16nf3drbfi6r6">
                    Entitlement
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-6571" data-mp="1bkoxvldod8lv">
                    Purchased
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-6572" data-mp="izffcz5i4uga">
                    Remaining
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-6573" data-mp="sw0i1du1ttgy">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-6574" data-mp="xotkfv21oppv">
                    Last Activity
                  </th>
                  <th className="px-6 py-3 w-10" data-id="element-6575" data-mp="54v4x6fhtfac"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E2E8F0]" data-id="element-6576" data-mp="3t9kznwy20a2g">
                {membersData.map(member => <tr key={member.id} className="hover:bg-[#F8FAFC]" data-id="element-6577" data-mp="3p90zslm3iscm">
                    <td className="px-6 py-4" data-id="element-6578" data-mp="6w2xw95qtpft">
                      <div data-id="element-6579" data-mp="6xxjzqsr3nlh">
                        <div className="text-sm font-bold text-[#0F172A]" data-id="element-6580" data-mp="1x03dine31m3">
                          {member.name}
                        </div>
                        <div className="text-xs text-[#64748B]" data-id="element-6581" data-mp="3adbdsngwuf7w">
                          {member.email}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4" data-id="element-6582" data-mp="3kbr3qn47bift">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${getTierColor(member.tier)}`} data-id="element-6583" data-mp="3k8d2xjb6rhah">
                        {member.tier}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-[#475569]" data-id="element-6584" data-mp="ulxcow9mho2p">
                      {member.entitlement}
                    </td>
                    <td className="px-6 py-4 text-right text-sm font-medium text-[#0F172A]" data-id="element-6585" data-mp="2chh8px0ktinc">
                      {member.purchased}
                    </td>
                    <td className="px-6 py-4 text-right text-sm text-[#64748B]" data-id="element-6586" data-mp="23ivu2cy86nru">
                      {member.remaining}
                    </td>
                    <td className="px-6 py-4" data-id="element-6587" data-mp="28hp57yq1i2ou">
                      <Badge variant={getStatusBadgeVariant(member.status)} data-id="element-6588" data-mp="2w6hvbejzdgbj">
                        {member.status}
                      </Badge>
                    </td>
                    <td className="px-6 py-4 text-sm text-[#64748B]" data-id="element-6589" data-mp="30afsvktnqj2h">
                      {member.lastActivity}
                    </td>
                    <td className="px-6 py-4" data-id="element-6590" data-mp="2rfhwu4v7qq6x">
                      <button className="text-[#94A3B8] hover:text-[#0F172A]" data-id="element-6591" data-mp="2re6t251rpift">
                        <MoreVertical className="w-4 h-4" data-id="element-6592" data-mp="3f4azsla95yb1" />
                      </button>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>

          <div className="px-6 py-4 border-t border-[#E2E8F0]" data-id="element-6593" data-mp="1vccgxp4xosxz">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4" data-id="element-6594" data-mp="1qb6lvknswtla">
              <div className="text-sm text-[#64748B]" data-id="element-6595" data-mp="1m9tj5jg096il">
                Showing 1–8 of 485 members
              </div>
              <div className="flex gap-2" data-id="element-6596" data-mp="1hbru8hvwecz7">
                <Button variant="outline" size="sm" disabled data-id="element-6597" data-mp="1hd31wssgs4vl">
                  Previous
                </Button>
                <Button variant="outline" size="sm" data-id="element-6598" data-mp="2e33p4nd6qdas">
                  Next
                </Button>
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* ROW 4: Orders Table */}
      <div className="mb-8" data-id="element-6599" data-mp="20a47aepxk702">
        <Card padding="none" data-id="element-6600" data-mp="208t52jc4v7b2">
          <div className="p-6 border-b border-[#E2E8F0]" data-id="element-6601" data-mp="209m40y4uosin">
            <h2 className="text-lg font-semibold text-[#0F172A]" data-id="element-6602" data-mp="2xpi1fxibdhd2">
              Recent Orders
            </h2>
          </div>
          <div className="overflow-x-auto" data-id="element-6603" data-mp="2swyq7773mybf">
            <table className="w-full" data-id="element-6604" data-mp="309o8wf64zpyd">
              <thead className="bg-[#F8FAFC]" data-id="element-6605" data-mp="30aprd4phfeq4">
                <tr data-id="element-6606" data-mp="30c0y9de852lg">
                  <th className="px-6 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-6607" data-mp="35897ka5qq1kp">
                    Order
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-6608" data-mp="3a9ym3zs68xdj">
                    Member
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-6609" data-mp="3j7r2l79mqb2i">
                    Products
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-6610" data-mp="3sxttn1x6xey7">
                    Qty
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-6611" data-mp="1tvcvplev0n3">
                    Total
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-6612" data-mp="aticq6yhrye1">
                    Date
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E2E8F0]" data-id="element-6613" data-mp="22vuwgxsdohtw">
                {ordersData.map(order => <tr key={order.id} className="hover:bg-[#F8FAFC]" data-id="element-6614" data-mp="2gmgi2u97v5vh">
                    <td className="px-6 py-4" data-id="element-6615" data-mp="2bug8sb79b0m8">
                      <a href="#" className="text-sm font-medium text-[#0EA5E9] hover:underline" data-id="element-6616" data-mp="2btnazzq9zlg8">
                        {order.id}
                      </a>
                    </td>
                    <td className="px-6 py-4 text-sm text-[#0F172A]" data-id="element-6617" data-mp="2ljzudomct8ks">
                      {order.member}
                    </td>
                    <td className="px-6 py-4 text-sm text-[#64748B]" data-id="element-6618" data-mp="2zi6kgil2a49w">
                      {order.products}
                    </td>
                    <td className="px-6 py-4 text-right text-sm text-[#0F172A]" data-id="element-6619" data-mp="155zxynmpw12a">
                      {order.qty}
                    </td>
                    <td className="px-6 py-4 text-right text-sm font-medium text-[#0F172A]" data-id="element-6620" data-mp="108zr4ai1mua4">
                      ${order.total}
                    </td>
                    <td className="px-6 py-4 text-sm text-[#64748B]" data-id="element-6621" data-mp="3guejlx0svzk5">
                      {order.date}
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
          <div className="p-4 border-t border-[#E2E8F0] text-center" data-id="element-6622" data-mp="373a48w77qcos">
            <button className="text-sm font-medium text-[#0EA5E9] hover:text-[#0284C7] hover:underline" data-id="element-6623" data-mp="2t4aetmbvltrk">
              View All Orders →
            </button>
          </div>
        </Card>
      </div>

      {/* Duplicate Allocation Modal */}
      {showDuplicateModal && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" data-id="element-6624" data-mp="2k56wikkj9ie4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-6625" data-mp="2o5ppwj22qwif">
            <div className="px-6 py-4 border-b border-[#E2E8F0]" data-id="element-6626" data-mp="ia4ze5oy3osd">
              <h3 className="text-lg font-semibold text-[#0F172A]" data-id="element-6627" data-mp="mcjhgnksikil">
                Duplicate Allocation
              </h3>
            </div>
            <div className="p-6 space-y-4" data-id="element-6628" data-mp="36bpynnk6w8o7">
              <div data-id="element-6629" data-mp="31btsbho0s55h">
                <label className="block text-sm font-medium text-[#0F172A] mb-2" data-id="element-6630" data-mp="31bajcqgckr3l">
                  Allocation Name
                </label>
                <Input value={duplicateName} onChange={e => setDuplicateName(e.target.value)} data-id="element-6631" data-mp="2rkz6geeor7kk" />
                <p className="text-xs text-[#64748B] mt-2" data-id="element-6632" data-mp="2njdwb0tjd7e2">
                  This creates a new draft allocation with the same products,
                  eligibility rules, and notification settings.
                </p>
              </div>
            </div>
            <div className="px-6 py-4 bg-[#F8FAFC] border-t border-[#E2E8F0] flex justify-end gap-3" data-id="element-6633" data-mp="2dlpy20wq1hr5">
              <Button variant="secondary" onClick={() => setShowDuplicateModal(false)} data-id="element-6634" data-mp="28no7yw0voi66">
                Cancel
              </Button>
              <Button variant="primary" onClick={() => {
            setShowDuplicateModal(false);
            navigate('/allocations/create');
          }} data-id="element-6635" data-mp="6uqina6b87pz">
                Create Duplicate
              </Button>
            </div>
          </div>
        </div>}

      {/* Close Allocation Modal */}
      {showCloseModal && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" data-id="element-6636" data-mp="o4c74g3v37ds">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-6637" data-mp="t2vwyrlkt3bf">
            <div className="px-6 py-4 border-b border-[#E2E8F0]" data-id="element-6638" data-mp="3sjc4eevp3s8a">
              <h3 className="text-lg font-semibold text-[#0F172A]" data-id="element-6639" data-mp="e87ejtsnrs2">
                Close Allocation
              </h3>
            </div>
            <div className="p-6 space-y-5" data-id="element-6640" data-mp="5cjkd3fdyiei">
              <div className="flex items-start gap-3 bg-[#FEF2F2] border border-[#FECACA] rounded-lg p-4" data-id="element-6641" data-mp="5fxj81ouc9h7">
                <AlertTriangle className="w-5 h-5 text-[#EF4444] flex-shrink-0 mt-0.5" data-id="element-6642" data-mp="1gsqazchmsxx9" />
                <p className="text-sm text-[#991B1B]" data-id="element-6643" data-mp="1gv41r95a3nz0">
                  This will immediately close the allocation and prevent any
                  further purchases.{' '}
                  <span className="font-semibold" data-id="element-6644" data-mp="3n89jvlki9rvj">
                    {allocationData.eligibleCount - allocationData.purchasedCount}{' '}
                    members
                  </span>{' '}
                  still have unclaimed entitlements.
                </p>
              </div>

              <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg p-4 space-y-2" data-id="element-6645" data-mp="3ifq6oppqcyr9">
                <div className="flex justify-between text-sm" data-id="element-6646" data-mp="33iz8gjrslt38">
                  <span className="text-[#64748B]" data-id="element-6647" data-mp="38jd4t4k17elf">Units claimed</span>
                  <span className="font-medium text-[#0F172A]" data-id="element-6648" data-mp="38gh79hc3irxi">
                    {allocationData.totalClaimed} of{' '}
                    {allocationData.totalAvailable}
                  </span>
                </div>
                <div className="flex justify-between text-sm" data-id="element-6649" data-mp="srz1d6p9ubd2">
                  <span className="text-[#64748B]" data-id="element-6650" data-mp="ssh9b0laqe0t">
                    Units released back to inventory
                  </span>
                  <span className="font-medium text-[#0F172A]" data-id="element-6651" data-mp="17uqs8e4s890o">
                    {allocationData.totalAvailable - allocationData.totalClaimed}
                  </span>
                </div>
              </div>

              <div className="flex items-start gap-3" data-id="element-6652" data-mp="1gnkkrd98p4wh">
                <div className="flex items-center h-5 mt-0.5" data-id="element-6653" data-mp="1goc7rzortub5">
                  <input id="notifyOnClose" type="checkbox" checked={notifyOnClose} onChange={e => setNotifyOnClose(e.target.checked)} className="w-4 h-4 text-[#0EA5E9] border-[#CBD5E1] rounded focus:ring-[#0EA5E9]" data-id="element-6654" data-mp="1bv9iw4uxv91a" />
                </div>
                <label htmlFor="notifyOnClose" className="text-sm text-[#0F172A]" data-id="element-6655" data-mp="1lo87hmqfqzkw">
                  Notify eligible members that this allocation has closed
                </label>
              </div>
            </div>
            <div className="px-6 py-4 bg-[#F8FAFC] border-t border-[#E2E8F0] flex justify-end gap-3" data-id="element-6656" data-mp="57vxeks2q3v5">
              <Button variant="secondary" onClick={() => setShowCloseModal(false)} data-id="element-6657" data-mp="16aeahoy4tci">
                Cancel
              </Button>
              <Button variant="danger" onClick={() => {
            setShowCloseModal(false);
            navigate('/allocations');
          }} data-id="element-6658" data-mp="20btzi389y5yi">
                Close Allocation
              </Button>
            </div>
          </div>
        </div>}
    </div>;
}